import { useForm } from "react-hook-form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useNetwork, useContractRead } from "wagmi";
import { useLiquidity } from "@/providers/LiquidityProvider";
import { CONTRACT_ADDRESSES } from "@/config/addresses";
import { factoryV6ABI } from '@/config/contracts';
import { formatEther } from 'viem';
import { Card, CardContent } from "@/components/ui/card";

interface FeeBreakdownProps {
  baseDeployFee: number;
  liquidityFee: number;
  estimatedGas: number;
  totalBnbRequired: number;
  initialLiquidity?: number;
}

export function FeeBreakdown({
  baseDeployFee = 0,
  liquidityFee = 0,
  estimatedGas = 0,
  totalBnbRequired = 0,
  initialLiquidity = 0
}: FeeBreakdownProps) {
  const { chain } = useNetwork();
  const { liquidity } = useLiquidity();

  // Get native currency based on chain
  const getNativeCurrency = () => {
    if (chain?.id === 56 || chain?.id === 97 || chain?.id === 204 || chain?.id === 5611) return 'BNB'; // BSC & opBNB
    if (chain?.id === 1 || chain?.id === 11155111) return 'ETH';
    if (chain?.id === 43114 || chain?.id === 43113) return 'AVAX';
    if (chain?.id === 137 || chain?.id === 80002) return 'MATIC'; // Polygon Mainnet & Amoy
    if (chain?.id === 11124 || chain?.id === 2741) return 'ETH'; // Abstract Testnet & Mainnet
    if (chain?.id === 8453 || chain?.id === 84532) return 'ETH'; // Base & Base Sepolia
    if (chain?.id === 10 || chain?.id === 11155420) return 'ETH'; // OP Mainnet & Sepolia
    if (chain?.id === 42161 || chain?.id === 421614) return 'ETH'; // Arbitrum One & Sepolia
    if (chain?.id === 1328 || chain?.id === 1329) return 'SEI'; // Sei Atlantic-2 Testnet & Mainnet
    return 'BNB'; // Default to BNB
  };

  // Get DEX name based on chain
  const getDexName = () => {
    if (chain?.id === 56 || chain?.id === 97 || chain?.id === 204 || chain?.id === 5611) return 'PancakeSwap'; // BSC & opBNB
    if (chain?.id === 1 || chain?.id === 11155111) return 'Uniswap';
    if (chain?.id === 43114 || chain?.id === 43113) return 'TraderJoe';
    if (chain?.id === 137 || chain?.id === 80002) return 'QuickSwap'; // Polygon
    if (chain?.id === 11124 || chain?.id === 2741) return 'Uniswap'; // Abstract
    if (chain?.id === 8453 || chain?.id === 84532) return 'Uniswap'; // Base
    if (chain?.id === 10 || chain?.id === 11155420) return 'Velodrome'; // OP
    if (chain?.id === 42161 || chain?.id === 421614) return 'Uniswap'; // Arbitrum
    return 'PancakeSwap'; // Default to PancakeSwap
  };

  // Read platform fee from contract
  const { data: platformFee } = useContractRead({
    address: chain?.id ? CONTRACT_ADDRESSES[chain.id]?.factory : undefined,
    abi: factoryV6ABI,
    functionName: 'platformFee',
    watch: true,
    enabled: !!chain?.id && !!CONTRACT_ADDRESSES[chain?.id]?.factory,
    onError: (error) => {
      console.error('FeeBreakdown - Error reading platform fee:', {
        chainId: chain?.id,
        factoryAddress: CONTRACT_ADDRESSES[chain?.id as keyof typeof CONTRACT_ADDRESSES]?.factory,
        error
      });
    },
    onSuccess: (data) => {
      console.log('FeeBreakdown - Platform fee read successfully:', {
        chainId: chain?.id,
        fee: data,
        formatted: formatEther(BigInt(data.toString()))
      });
    }
  });

  // Format platform fee from Wei to native currency
  const formattedPlatformFee = platformFee ? Number(formatEther(BigInt(platformFee.toString()))) : 0;

  // Ensure all values are valid numbers
  const safeBaseDeployFee = isNaN(baseDeployFee) ? 0 : baseDeployFee;
  const safeLiquidityFee = isNaN(liquidityFee) ? 0 : liquidityFee;
  const safeEstimatedGas = isNaN(estimatedGas) ? 0 : estimatedGas;
  const safeInitialLiquidity = isNaN(initialLiquidity) ? 0 : initialLiquidity;

  // Calculate total with safe values
  const calculatedTotal = safeBaseDeployFee + safeLiquidityFee + safeEstimatedGas + safeInitialLiquidity;

  // If chain is not connected, show a message
  if (!chain?.id) {
    return (
      <div className="space-y-4">
        <h3 className="font-medium">Fee Breakdown</h3>
        <div className="text-sm text-muted-foreground">
          Please connect your wallet to view fee breakdown
        </div>
      </div>
    );
  }

  const currency = getNativeCurrency();
  const dexName = getDexName();

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="space-y-2">
          {/* First Transaction */}
          <div className="p-3 rounded-md bg-secondary/50 space-y-2">
            <div className="text-sm font-medium">First Transaction (Token Creation)</div>
            <div className="text-xs text-muted-foreground mb-2">Creates your token contract on the blockchain</div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Platform Fee</span>
              <span>{formattedPlatformFee.toFixed(5)} {currency}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Network Fee (Est.)</span>
              <span>{estimatedGas.toFixed(4)} {currency}</span>
            </div>
          </div>
          
          {/* Second Transaction */}
          {initialLiquidity > 0 && (
            <>
              <div className="p-3 rounded-md bg-secondary/50 space-y-2">
                <div className="text-sm font-medium">Second Transaction (Token Approval)</div>
                <div className="text-xs text-muted-foreground mb-2">Grants permission to {dexName} to use your tokens for adding liquidity</div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Network Fee (Est.)</span>
                  <span>{(estimatedGas * 0.5).toFixed(4)} {currency}</span>
                </div>
              </div>

              {/* Third Transaction */}
              <div className="p-3 rounded-md bg-secondary/50 space-y-2">
                <div className="text-sm font-medium">Third Transaction (Add Liquidity)</div>
                <div className="text-xs text-muted-foreground mb-2">Creates the trading pair on {dexName}</div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Initial Liquidity</span>
                  <span>{initialLiquidity.toFixed(4)} {currency}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Platform Fee ({((safeLiquidityFee / safeInitialLiquidity) * 100).toFixed(1)}%)
                  </span>
                  <span>{safeLiquidityFee.toFixed(4)} {currency}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Network Fee (Est.)</span>
                  <span>{safeEstimatedGas.toFixed(4)} {currency}</span>
                </div>
              </div>
            </>
          )}

          <div className="h-px bg-border my-2" />

          {/* Total */}
          <div className="p-3 rounded-md bg-primary/10 space-y-2">
            <div className="text-sm font-medium">Total Required {currency}</div>
            <div className="text-xs text-muted-foreground mb-2">Total amount of {currency} needed for all transactions</div>
            <div className="flex justify-between text-sm font-medium">
              <span>Total</span>
              <span>{calculatedTotal.toFixed(4)} {currency}</span>
            </div>
          </div>

          {initialLiquidity > 0 && (
            <div className="text-xs text-muted-foreground mt-2">
              Note: You will need to approve 3 separate transactions. This is required for security and to interact with {dexName} properly.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}